import type { FinalPlan, PlanPeriod } from 'components';
import { JSX } from 'react';

import UpgradeCreditPlanButton from './UpgradeCreditPlanCardButton';
import UpgradeCreditPlanDetails from './UpgradeCreditPlanCardDetails';
import UpgradeCreditPlanCardHeader from './UpgradeCreditPlanCardHeader';
import UpgradeCreditTierSelector from './UpgradeCreditTierSelector';

export interface UpgradeCreditPlanCardProps {
    plan: FinalPlan;
    selectedPeriod: PlanPeriod;
    currencySymbol: string;
    isCurrentPlan: boolean;
    isCancelledOrExpired: boolean;
    selectedTier: { planId: string; planCode: string; credits: number } | null;
    onSelectTier: (planCode: string | undefined, credits: number) => void;
    onUpgradePlan: (showConfirmationModal: boolean) => void;
    currentBillingAmount: number | null;
    currentAddonCode?: string;
}

export default function UpgradeCreditPlanCard({
    plan,
    selectedPeriod,
    currencySymbol,
    isCurrentPlan,
    isCancelledOrExpired,
    selectedTier,
    onSelectTier,
    onUpgradePlan,
    currentBillingAmount,
    currentAddonCode,
}: UpgradeCreditPlanCardProps): JSX.Element {
    const price = selectedPeriod === 'monthly' ? plan.pricing.monthly : plan.pricing.annual;

    const { downloads, mediaTypes, AIToolkit, workflowTools } = plan.features;

    return (
        <div className="text-left rounded-lg py-10 px-6 flex flex-col gap-6 w-auto min-w-[320px] md:max-w-[362px] flex-1 h-full bg-white">
            <UpgradeCreditPlanCardHeader
                planName={plan.name}
                price={price ?? 0}
                selectedPeriod={selectedPeriod}
                currencySymbol={currencySymbol}
            />

            {plan.creditTiers.length > 0 && (
                <div className="mb-4">
                    <UpgradeCreditTierSelector
                        creditTiers={plan.creditTiers}
                        selectedPeriod={selectedPeriod}
                        currencySymbol={currencySymbol}
                        isDisabled={false}
                        selectedTier={selectedTier}
                        onChange={(planCode, credits) => onSelectTier(planCode, credits)}
                    />
                </div>
            )}

            <UpgradeCreditPlanButton
                selectedPlanCode={selectedTier?.planCode ?? null}
                isCurrentPlan={isCurrentPlan}
                isCancelledOrExpired={isCancelledOrExpired}
                onUpgradePlan={onUpgradePlan}
                plan={plan}
                selectedPeriod={selectedPeriod}
                currentBillingAmount={currentBillingAmount}
                currentAddonCode={currentAddonCode}
            />

            <UpgradeCreditPlanDetails
                licenseType={plan.licenseType}
                AIToolkit={AIToolkit}
                downloads={downloads}
                mediaTypes={mediaTypes}
                workflowTools={workflowTools}
            />
            {/* Flexible spacer that grows to fill available vertical space, to ensure bottom alignment */}
            <div className="flex-grow"></div>
        </div>
    );
}
